/**
 * Norwegian formatting helpers — dokumentasjon/06-designsystem.md requires
 * "man. 20. jul", 24-hour clock and Europe/Oslo everywhere.
 */

export const OSLO = 'Europe/Oslo';
const LOCALE = 'nb-NO';

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

/** "man. 20. jul." */
export function formatDate(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: OSLO,
  }).format(toDate(value));
}

/** "20. juli 2026" */
export function formatDateLong(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: OSLO,
  }).format(toDate(value));
}

/** "20. jul. 14:30" */
export function formatDateTime(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: OSLO,
  }).format(toDate(value));
}

/** "14:30" */
export function formatTime(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: OSLO,
  }).format(toDate(value));
}

/** "om 3 dager" / "for 2 timer siden" */
export function formatRelative(value: Date | string | number, now: Date = new Date()): string {
  const target = toDate(value);
  const diffMs = target.getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 3600_000],
    ['month', 30 * 24 * 3600_000],
    ['day', 24 * 3600_000],
    ['hour', 3600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (Math.abs(diffMs) >= ms) {
      return rtf.format(Math.round(diffMs / ms), unit);
    }
  }
  return 'nå nettopp';
}

/** Whole days between today and the given date (negative = in the past). */
export function daysUntil(value: Date | string | number, now: Date = new Date()): number {
  const target = toDate(value);
  const startOfDay = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((startOfDay(target) - startOfDay(now)) / 86_400_000);
}

/** "1 234" — Norwegian thin-space grouping. */
export function formatNumber(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits }).format(value);
}

/** "87 %" — note the Norwegian space before the sign. */
export function formatPercent(value: number, maximumFractionDigits = 0): string {
  return `${formatNumber(value, maximumFractionDigits)} %`;
}

/** "3 forslag" / "1 forslag" — picks singular or plural. */
export function pluralize(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/** "Skjema og sykehus" → "skjema-og-sykehus" — æøå transliterated, not dropped. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
